import { useConstant } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import { isInstallingChildApp$ } from '@op/app-shared'
import type { FeatureRequirements } from '@op/services'
import { useEffect } from 'react'
import type { GuardModalFeature } from '../../types'
import { useTypedSelector } from '../../types'
import { InfoModal } from '../../widgets'
import { ChildAppInstallBtn } from './ChildAppInstallBtn'

type ChildAppInstallModalProps = {
	readonly deviceId: string
	readonly feature?: GuardModalFeature
	readonly onClose: () => void
	readonly userId: string
}

type FeatureRequiringChildApp = {
	[K in GuardModalFeature]: (typeof FeatureRequirements)[K] extends { requireChildApp: true } ? K
	:	never
}[GuardModalFeature]

const ChildAppInstallModal = ({
	deviceId,
	feature,
	onClose,
	userId,
}: ChildAppInstallModalProps) => {
	const [t] = useT('devices')

	const isInstallingChildApp = useTypedSelector((s) =>
		isInstallingChildApp$(s, { deviceId, userId }),
	)
	const initialInstallState = useConstant(() => isInstallingChildApp)

	// Close modal if this was opened without the app installing, and installing starts.
	const shouldClose = !initialInstallState && isInstallingChildApp

	const featureRequiresText = (() => {
		if (feature == null) return null

		const featureNameMap = {
			ALLOWANCE: 'allowance',
			VEW: 'view',
		} satisfies Record<FeatureRequiringChildApp, string>

		if (feature in featureNameMap) {
			const featureKey = featureNameMap[feature as FeatureRequiringChildApp]

			return t('child_app_installed_modal.feature_requires', {
				feature: t.$(`connect.connect_comparison.features.${featureKey}`),
			})
		}

		return null
	})()

	useEffect(() => {
		if (shouldClose) onClose()
	}, [shouldClose])

	// FIXME: Translate
	return (
		<InfoModal hideClose={false} header="OurPact Jr." onClose={onClose}>
			{featureRequiresText != null && <p>{featureRequiresText}</p>}

			<p>
				{t(
					isInstallingChildApp ?
						'child_app_installed_modal.description'
					:	'device.install_child_app_description',
				)}
			</p>

			<InfoModal.Footer>
				<ChildAppInstallBtn disabled={isInstallingChildApp} childId={userId} deviceId={deviceId} />
			</InfoModal.Footer>
		</InfoModal>
	)
}

export default ChildAppInstallModal
